// Nutrition › Journal « Photos et ressentis » (no counting, no calorie): the seven days up to the demo today, mode Photos /
// Ressentis (UBM SegmentedControl), one entry per meal (photo or « Non renseigné »), « Ajouter un repas » (drawer), the member's
// own tick « Journée entièrement renseignée » (never automatic, unticked today) and an independent coach-sharing switch.
import { Button } from "@ubm/ui/components/button";
import { useState } from "react";
import { Camera, ImagePlus, Plus, Utensils } from "lucide-react";
import { Avatar } from "@ubm/ui/components/avatar";
import { Checkbox } from "@ubm/ui/components/checkbox";
import { SegmentedControl } from "@ubm/ui/components/segmented-control";
import { Switch } from "@ubm/ui/components/switch";
import { Textarea } from "@ubm/ui/components/textarea";
import { AFTER, HUNGER, JOURNAL_DAYS, JOURNAL_TODAY, MEALS, MEAL_LABEL, RECIPES, type JournalEntry, type MealId } from "./nutrition-fixtures";
import { TEAM } from "./studio-fixtures";
import { DayStrip, NutriSheet, nutri, useDaySwap, useNutri } from "./nutrition-parts";

const COACH = TEAM.find(c => c.mine) ?? TEAM[0]!;
const COACH_FIRST = COACH.firstName.split(" ")[0]!;
/** Demo photos the simulated camera hands back (fictitious, the same generated food images as the recipes). */
const DEMO_PHOTOS = RECIPES.flatMap(r => r.photo ? [r.photo] : []);
let savedDay = JOURNAL_TODAY;
let savedMode: "photos" | "ressentis" = "photos";

interface Draft { meal: MealId; photo?: string; hunger?: JournalEntry["hunger"]; after?: JournalEntry["after"]; note: string }

export function NutritionJournal() {
  const s = useNutri();
  const [day, setDayState] = useState(savedDay);
  const setDay = (d: string) => { savedDay = d; setDayState(d); };
  const [mode, setModeState] = useState(savedMode);
  const setMode = (m: "photos" | "ressentis") => { savedMode = m; setModeState(m); };
  const [draft, setDraft] = useState<Draft | null>(null);
  const swap = useDaySwap(day, JOURNAL_DAYS.map(d => d.key));
  const d = JOURNAL_DAYS.find(x => x.key === day) ?? JOURNAL_DAYS[0]!;
  const entries = s.journal[day] ?? {};
  const complete = s.complete.includes(day);

  const open = (meal?: MealId) => {
    const m = meal ?? MEALS.find(x => !entries[x]) ?? MEALS[0]!;
    const e = entries[m];
    setDraft({ meal: m, photo: e?.photo, hunger: e?.hunger, after: e?.after, note: e?.note ?? "" });
  };
  const save = () => {
    if (!draft) return;
    const entry: JournalEntry = { photo: draft.photo, hunger: draft.hunger, after: draft.after, note: draft.note.trim() || undefined };
    const journal = structuredClone(s.journal);
    journal[day] = { ...journal[day], [draft.meal]: entry };
    nutri.set({ journal });
    setDraft(null);
    nutri.toast(`${MEAL_LABEL[draft.meal]} renseigné`);
  };
  const empty = (x: Draft) => !x.photo && !x.hunger && !x.after && !x.note.trim();

  return <>
    <DayStrip label="Sept derniers jours" days={JOURNAL_DAYS} value={day} onChange={setDay} today={JOURNAL_TODAY}
      mark={k => s.complete.includes(k) ? "done" : Object.keys(s.journal[k] ?? {}).length ? "dot" : null} />

    <SegmentedControl className="ccn-seg" label="Mode du journal" value={mode} onValueChange={v => setMode(v as "photos" | "ressentis")}
      options={[{ value: "photos", label: "Photos" }, { value: "ressentis", label: "Ressentis" }]} />

    <div className="ccn-share">
      <Avatar name={COACH.firstName} src={COACH.photo} size="md" />
      <span className="ccn-share-text"><b>Partager avec {COACH_FIRST}</b><small>{s.share ? `${COACH_FIRST} voit tes photos et ressentis.` : "Ton journal reste privé."}</small></span>
      <Switch size="small" checked={s.share} onCheckedChange={v => { nutri.set({ share: v }); nutri.toast(v ? `Journal partagé avec ${COACH_FIRST}` : "Partage désactivé"); }} aria-label={`Partager mon journal avec ${COACH_FIRST}`} />
    </div>

    <div ref={swap} className="ccn-daycontent">
      <div className="ccn-dayhead">
        <h2 className="ccn-h2">{d.long[0]!.toUpperCase() + d.long.slice(1)}</h2>
        {day === JOURNAL_TODAY && <span className="ccn-tag">Aujourd'hui</span>}
      </div>
      <ul className="ccn-journal" data-mode={mode}>{MEALS.map(m => { const e = entries[m]; return <li key={m}>
        <button type="button" className="ccn-jentry" data-empty={!e || undefined} onClick={() => open(m)}>
          {mode === "photos" && (e?.photo ? <img className="ccn-jentry-img" src={e.photo} alt="" />
            : <span className="ccn-jentry-img ccn-ph" aria-hidden="true">{e ? <Camera size={20} strokeWidth={1.6} /> : <Utensils size={20} strokeWidth={1.6} />}</span>)}
          <span className="ccn-jentry-body">
            <span className="ccn-over">{MEAL_LABEL[m]}</span>
            {!e ? <b className="ccn-muted">Non renseigné</b>
              : mode === "photos" ? <b>{e.photo ? "Photo ajoutée" : "Sans photo"}</b>
              : <span className="ccn-feel">{e.hunger && <span>Avant : {e.hunger}</span>}{e.after && <span>Après : {e.after}</span>}{!e.hunger && !e.after && <span>Pas de ressenti noté</span>}</span>}
            {e?.note && <small className="ccn-jnote">« {e.note} »</small>}
          </span>
          <Plus size={18} strokeWidth={2} aria-hidden="true" className="ccn-jentry-plus" data-hide={e ? true : undefined} />
        </button>
      </li>; })}</ul>

      <div className="ccn-complete"><Checkbox label="Journée entièrement renseignée" checked={complete}
        onCheckedChange={v => nutri.set({ complete: v === true ? [...s.complete.filter(x => x !== day), day] : s.complete.filter(x => x !== day) })} /></div>
      <Button variant="primary" size="lg" className="cc-cta" onClick={() => open()}><Plus size={17} strokeWidth={2.4} /> Ajouter un repas</Button>
    </div>

    {draft && <NutriSheet title={entries[draft.meal] ? "Modifier ce repas" : "Ajouter un repas"} onClose={() => setDraft(null)}>
      <p className="ccn-sheet-sub">{d.long[0]!.toUpperCase() + d.long.slice(1)}</p>
      <p className="ccn-sheet-label">Repas</p>
      <div className="ccn-choice">{MEALS.map(m => <button key={m} type="button" className="cc-chip" aria-pressed={draft.meal === m} data-active={draft.meal === m || undefined}
        onClick={() => { const e = entries[m]; setDraft({ meal: m, photo: e?.photo, hunger: e?.hunger, after: e?.after, note: e?.note ?? "" }); }}>{MEAL_LABEL[m]}</button>)}</div>
      <p className="ccn-sheet-label">Photo</p>
      {draft.photo ? <div className="ccn-jphoto"><img src={draft.photo} alt="Photo du repas" /><button type="button" className="ccn-textbtn" onClick={() => setDraft({ ...draft, photo: undefined })}>Retirer la photo</button></div>
        : <Button variant="secondary" size="sm" className="ccn-outline--wide" onClick={() => setDraft({ ...draft, photo: DEMO_PHOTOS[MEALS.indexOf(draft.meal) % DEMO_PHOTOS.length] })}><ImagePlus size={16} strokeWidth={2} /> Ajouter une photo</Button>}
      <p className="ccn-sheet-label">Avant le repas</p>
      <div className="ccn-choice">{HUNGER.map(h => <button key={h} type="button" className="cc-chip" aria-pressed={draft.hunger === h} data-active={draft.hunger === h || undefined} onClick={() => setDraft({ ...draft, hunger: draft.hunger === h ? undefined : h })}>{h}</button>)}</div>
      <p className="ccn-sheet-label">Après le repas</p>
      <div className="ccn-choice">{AFTER.map(h => <button key={h} type="button" className="cc-chip" aria-pressed={draft.after === h} data-active={draft.after === h || undefined} onClick={() => setDraft({ ...draft, after: draft.after === h ? undefined : h })}>{h}</button>)}</div>
      <Textarea label="Note" placeholder="Ce que tu veux retenir, si tu veux" rows={2} value={draft.note} onChange={e => setDraft({ ...draft, note: e.target.value })} />
      <Button variant="primary" size="lg" className="cc-cta" disabled={empty(draft)} onClick={save}>Enregistrer</Button>
      <p className="ccn-sheet-sub">Prototype : la photo est une image de démonstration, rien n'est envoyé.</p>
    </NutriSheet>}
  </>;
}
